import React from 'react';
import FormSection from './FormSection';

class LegalSection extends FormSection {
  validate() {
    const errors = [];
    const { watch } = this.props;
    
    const incorporated = watch('incorporated');
    
    if (!incorporated) errors.push('Incorporation status is required');
    
    if (incorporated === 'yes') {
      const ipAssignment = watch('ipAssignment');
      const founderVesting = watch('founderVesting');
      const founderBoardSeats = watch('founderBoardSeats');
      
      if (ipAssignment === undefined) errors.push('IP Assignment status is required');
      if (founderVesting === undefined) errors.push('Founder vesting status is required');
      if (founderBoardSeats === undefined) errors.push('Founder board seats status is required');
      
      // Check if any are "no" and openToAmending is required
      if (ipAssignment === false || founderVesting === false || founderBoardSeats === false) {
        const openToAmending = watch('openToAmending');
        if (!openToAmending) errors.push('Response to amending corporate documents is required');
      }
    }
    
    if (incorporated === 'no') {
      const incorporationAgreement = watch('incorporationAgreement');
      if (!incorporationAgreement) errors.push('Incorporation agreement is required');
    }

    return errors;
  }

  detectLegalFlags() {
    const flags = [];
    const { watch } = this.props;
    
    const incorporated = watch('incorporated');
    
    if (incorporated === 'yes') {
      const ipAssignment = watch('ipAssignment');
      const founderVesting = watch('founderVesting');
      const founderBoardSeats = watch('founderBoardSeats');
      const openToAmending = watch('openToAmending');
      
      // Flag: Missing standard venture terms
      if (!ipAssignment || !founderVesting || !founderBoardSeats) {
        flags.push({
          type: 'yellow',
          message: 'Missing standard venture terms → Corporate structure needs updating for venture funding'
        });
      }
      
      // Flag: Not open to amending
      if ((!ipAssignment || !founderVesting || !founderBoardSeats) && openToAmending === 'no') {
        flags.push({
          type: 'yellow',
          message: 'Not open to amending corporate documents → May block venture funding opportunities'
        });
      }
    }
    
    if (incorporated === 'no') {
      const incorporationAgreement = watch('incorporationAgreement');
      
      // Flag: Would like to discuss incorporation
      if (incorporationAgreement === 'discuss') {
        flags.push({
          type: 'yellow',
          message: 'Would like to discuss incorporation → May indicate hesitation about standard venture terms'
        });
      }
    }
    
    return flags;
  }

  render() {
    const { watch } = this.props;
    const incorporated = watch('incorporated');
    const ipAssignment = watch('ipAssignment');
    const founderVesting = watch('founderVesting');
    const founderBoardSeats = watch('founderBoardSeats');
    const legalFlags = this.detectLegalFlags();

    return (
      <div className="border border-gray-200 rounded-lg p-6 mb-6">
        {this.renderSectionHeader(
          'Part 4: Legal & Corporate Structure',
          'Ensuring your company is set up for venture-scale success is a core part of the Version Bravo program. Please answer the following questions honestly.'
        )}
        
        <div className="space-y-6">
          {/* Incorporation Status */}
          <div>
            {this.renderField({
              name: 'incorporated',
              label: 'Is your company incorporated?',
              type: 'radio',
              required: true,
              options: [
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' }
              ]
            })}
          </div>

          {/* Conditional: If incorporated = YES */}
          {incorporated === 'yes' && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <h4 className="text-lg font-medium text-blue-900 mb-4">
                Corporate Structure Requirements
              </h4>
              <p className="text-sm text-blue-700 mb-4">
                Does your current corporate structure include all of the following?
              </p>
              
              <div className="space-y-4">
                {/* IP Assignment */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    IP Assignment (All IP developed by founders/employees has been assigned to the company by an IP Assignment agreement)
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        {...this.props.register('ipAssignment', { required: 'IP Assignment status is required' })}
                        type="radio"
                        value="true"
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">Yes</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        {...this.props.register('ipAssignment', { required: 'IP Assignment status is required' })}
                        type="radio"
                        value="false"
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">No</span>
                    </label>
                  </div>
                </div>

                {/* Founder Vesting */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Founder Shares: There are vesting schedules for all founders
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        {...this.props.register('founderVesting', { required: 'Founder vesting status is required' })}
                        type="radio"
                        value="true"
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">Yes</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        {...this.props.register('founderVesting', { required: 'Founder vesting status is required' })}
                        type="radio"
                        value="false"
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">No</span>
                    </label>
                  </div>
                </div>

                {/* Founder Board Seats */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Founder Board Seats: Founder Board seats are tied to employment/service with the company
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        {...this.props.register('founderBoardSeats', { required: 'Founder board seats status is required' })}
                        type="radio"
                        value="true"
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">Yes</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        {...this.props.register('founderBoardSeats', { required: 'Founder board seats status is required' })}
                        type="radio"
                        value="false"
                        className="mr-2"
                      />
                      <span className="text-sm text-gray-700">No</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Conditional: If any of the above are "No" */}
              {(ipAssignment === 'false' || founderVesting === 'false' || founderBoardSeats === 'false') && (
                <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                  <h5 className="text-sm font-medium text-yellow-800 mb-2">
                    Amendment Required
                  </h5>
                  {this.renderField({
                    name: 'openToAmending',
                    label: 'If you answered NO to any of the above, are you open to amending your corporate documents to include these standard venture terms?',
                    type: 'radio',
                    required: true,
                    options: [
                      { value: 'yes', label: 'Yes' },
                      { value: 'no', label: 'No, please explain:' }
                    ]
                  })}
                  
                  {this.renderConditionalField(
                    { field: 'openToAmending', equals: 'no' },
                    {
                      name: 'amendingExplanation',
                      label: 'Please explain why you are not open to amending',
                      type: 'textarea',
                      required: true,
                      placeholder: 'Explain your concerns about amending corporate documents'
                    }
                  )}
                </div>
              )}
            </div>
          )}

          {/* Conditional: If incorporated = NO */}
          {incorporated === 'no' && (
            <div className="bg-orange-50 border border-orange-200 rounded-md p-4">
              <h4 className="text-lg font-medium text-orange-900 mb-4">
                Incorporation Agreement
              </h4>
              {this.renderField({
                name: 'incorporationAgreement',
                label: 'If you are NOT incorporated, do you agree to sign a side letter obligating you to incorporate with the standard venture terms listed above (IP Assignment, Vesting, Board Structure) prior to the start of the program?',
                type: 'radio',
                required: true,
                options: [
                  { value: 'agree', label: 'I agree.' },
                  { value: 'discuss', label: 'I would like to discuss this further.' }
                ]
              })}
              
              {this.renderConditionalField(
                { field: 'incorporationAgreement', equals: 'discuss' },
                {
                  name: 'incorporationConcerns',
                  label: 'Please describe what you would like to discuss',
                  type: 'textarea',
                  required: true,
                  placeholder: 'Describe your concerns or questions about incorporation'
                }
              )}
            </div>
          )}

          {/* Legal Flags */}
          {legalFlags.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Legal Structure Flags Detected
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <ul className="list-disc list-inside space-y-1">
                      {legalFlags.map((flag, index) => (
                        <li key={index}>{flag.message}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="mt-3 text-sm text-yellow-700">
                    <p className="font-medium">
                      These flags may require manual review during the application process.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Additional Legal Context */}
          <div>
            {this.renderField({
              name: 'legalAdditionalInfo',
              label: 'Additional Legal Information',
              type: 'textarea',
              required: false,
              placeholder: 'Any additional legal context or concerns you would like to share (optional)'
            })}
          </div>
        </div>
      </div>
    );
  }
}

export default LegalSection;